import { useState, useEffect } from 'react';

const CURRENT_VERSION = '1.0';

export function useCookieConsent() {
  const [hasConsent, setHasConsent] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadConsent = () => {
      try {
        const stored = localStorage.getItem('cookie-consent');
        if (stored) {
          const parsed = JSON.parse(stored);

          // Check if consent is still valid (not expired, same version)
          if (parsed.version === CURRENT_VERSION) {
            setHasConsent(true);
            setPreferences(parsed.preferences);
          } else {
            // Version mismatch, clear old consent
            localStorage.removeItem('cookie-consent');
            localStorage.removeItem('cookie-consent-timestamp');
          }
        }
      } catch (error) {
        console.error('Error loading cookie consent:', error);
        localStorage.removeItem('cookie-consent');
        localStorage.removeItem('cookie-consent-timestamp');
      } finally {
        setIsLoading(false);
      }
    };

    loadConsent();
  }, []);

  const saveConsent = (newPreferences) => {
    const consentData = {
      preferences: newPreferences,
      timestamp: new Date().toISOString(),
      version: CURRENT_VERSION,
    };

    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    localStorage.setItem('cookie-consent-timestamp', consentData.timestamp);

    setHasConsent(true);
    setPreferences(newPreferences);

    // tell the Google tag every choice, a no as well as a yes (the default is set in public/index.html)
    applyConsent(newPreferences);
  };

  const clearConsent = () => {
    localStorage.removeItem('cookie-consent');
    localStorage.removeItem('cookie-consent-timestamp');
    setHasConsent(false);
    const none = { essential: true, analytics: false, marketing: false };
    setPreferences(none);
    applyConsent(none);
  };

  // Google consent mode: ads and analytics storage follow the visitor's choice
  const applyConsent = (prefs) => {
    if (!window.gtag) return;
    const ads = prefs.marketing ? 'granted' : 'denied';
    window.gtag('consent', 'update', {
      analytics_storage: prefs.analytics ? 'granted' : 'denied',
      ad_storage: ads,
      ad_user_data: ads,
      ad_personalization: ads,
    });
  };

  return {
    hasConsent,
    preferences,
    isLoading,
    saveConsent,
    clearConsent,
    canUseAnalytics: hasConsent && preferences.analytics,
    canUseMarketing: hasConsent && preferences.marketing,
  };
}
